import { v4 as uuidv4 } from "uuid";
import { DEFAULT_DISPATCH_ITEM, BASE_TASK_TYPES, COMMON_LOCATIONS } from "../constants/dispatchConstants";

/**
 * Creates a new dispatch item with unique ID
 * @returns {Object} - New dispatch item
 */
export const createNewDispatchItem = () => ({
  id: uuidv4(),
  ...DEFAULT_DISPATCH_ITEM,
  checklists: [], // Initialize empty checklists array
});

/**
 * Formats date for Firestore document path
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string (YYYY-MM-DD)
 */
export const formatDateForFirestore = (date) => {
  return new Date(date).toISOString().split("T")[0];
};

/**
 * Generates task types with dynamic locations based on rooms and employee type
 * @param {Array} roomsList - List of all rooms
 * @param {string} employeeType - Type of employee
 * @returns {Array} - Task types with populated locations
 */
export const generateTaskTypes = (roomsList = [], employeeType = "") => {
  return BASE_TASK_TYPES.map((taskType) => {
    let taskLocations = [...taskType.taskLocations];

    // Populate Room Turn locations based on employee type
    if (taskType.task === "Room Turn") {
      // Filter out rooms with status "Up" or "Occupied"
      const availableRooms = roomsList.filter(
        (room) => room.status !== "Up" && room.status !== "Occupied"
      );

      if (employeeType === "housekeeping") {
        taskLocations = availableRooms.map(
          (room) => `${room.displayName} - ${room.selectedHousekeepingRequirements}`
        );
      } else if (employeeType === "maintenance") {
        taskLocations = availableRooms.map(
          (room) => `${room.displayName} - ${room.selectedMaintenanceRequirements}`
        );
      } else {
        taskLocations = availableRooms.map((room) => room.displayName);
      }
    }

    // Populate Work Orders locations with buildings
    if (taskType.task === "Work Orders") {
      const buildings = [...new Set(roomsList.map((r) => r.Building))];
      taskLocations = [...buildings, COMMON_LOCATIONS.ALL_BUILDINGS];
    }

    return {
      ...taskType,
      taskLocations,
    };
  });
};

/**
 * Generates dispatch data for Firestore storage
 * @param {Object} item - Dispatch item
 * @returns {Object} - Formatted data for Firestore
 */
export const generateDispatchData = (item) => {
  const isRoomTurn = item.task === "Room Turn";
  const startTime = Number(new Date(item.startTime).getTime()).toString();
  const endTime = Number(new Date(item.endTime).getTime()).toString();
  const notes = item.notes || "";
  const checklists = item.checklists || [];

  if (isRoomTurn) {
    return {
      isRoom: true,
      room: item.location,
      startTime,
      endTime,
      notes,
      checklists,
    };
  } else {
    return {
      isRoom: false,
      primary: item.task,
      secondary: item.location,
      startTime,
      endTime,
      notes,
      checklists,
    };
  }
};

/**
 * Validates a dispatch item
 * @param {Object} item - Dispatch item to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateDispatchItem = (item) => {
  const errors = [];

  if (!item.startTime) {
    errors.push("Start time is required");
  }

  if (!item.endTime) {
    errors.push("End time is required");
  }

  if (!item.task) {
    errors.push("Task is required");
  }

  if (!item.location && item.task !== "Other") {
    errors.push("Location is required");
  }

  if (item.startTime && item.endTime) {
    const start = new Date(item.startTime);
    const end = new Date(item.endTime);
    if (end <= start) {
      errors.push("End time must be after start time");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Calculates total hours for a dispatch item
 * @param {Object} item - Dispatch item
 * @returns {number} - Total hours
 */
export const calculateDispatchHours = (item) => {
  if (!item.startTime || !item.endTime) return 0;

  const start = new Date(item.startTime);
  const end = new Date(item.endTime);
  const diffMs = end - start;
  return Math.max(0, diffMs / (1000 * 60 * 60)); // Convert to hours
};

/**
 * Converts Firestore dispatch data back to UI format
 * @param {Object} firestoreData - Data from Firestore
 * @param {string} docId - Document ID
 * @returns {Object} - UI-formatted dispatch item
 */
export const convertFirestoreToUI = (firestoreData, docId) => {
  const converted = {
    id: docId,
    notes: firestoreData.notes || "",
    status: "pending", // Default status since original doesn't store this
    completed: false,
    checklists: firestoreData.checklists || [], // Include checklists
  };

  // Convert timestamps back to Date objects
  if (firestoreData.startTime) {
    const startTimestamp = parseInt(firestoreData.startTime, 10);
    converted.startTime = new Date(startTimestamp);
  } else {
    converted.startTime = "";
  }

  if (firestoreData.endTime) {
    const endTimestamp = parseInt(firestoreData.endTime, 10);
    converted.endTime = new Date(endTimestamp);
  } else {
    converted.endTime = "";
  }

  // Handle different data structures
  if (firestoreData.isRoom) {
    // Room turn task
    converted.task = "Room Turn";
    converted.location = firestoreData.room || "";
  } else {
    // Other tasks
    converted.task = firestoreData.primary || "";
    converted.location = firestoreData.secondary || "";
  }

  return converted;
};